import type { StatusTone } from '@vnetwork/ui/data';

/**
 * Shared view helpers for the Cloud drill-down pages (zones / hosts /
 * datastores / instances). Maps ServiceMgmt status strings → StatusPill tone,
 * and derives host/datastore usage percentages. Kept side-effect free so every
 * cloud page can share it. Formatting numbers reuses `../dashboard/capacity-format`.
 */

/** ServiceMgmt provider/host/instance status → StatusPill tone + label. */
export function cloudStatusView(status?: string): { tone: StatusTone; label: string } {
  const key = (status ?? '').toLowerCase();
  switch (key) {
    case 'healthy':
    case 'active':
    case 'running':
    case 'connected':
    case 'poweredon':
    case 'ok':
      return { tone: 'success', label: humanize(status) };
    case 'warning':
    case 'degraded':
    case 'suspended':
      return { tone: 'warning', label: humanize(status) };
    case 'error':
    case 'critical':
    case 'unreachable':
    case 'poweredoff':
    case 'stopped':
      return { tone: 'danger', label: humanize(status) };
    case 'unknown':
    case '':
      return { tone: 'neutral', label: status ? humanize(status) : '—' };
    default:
      return { tone: 'info', label: humanize(status) };
  }
}

/** Title-case a status token (`poweredOn` / `in_use` → readable). */
function humanize(value?: string): string {
  if (!value) return '—';
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Memory MB → GB (1 decimal), null-safe. */
export function mbToGb(mb: number | null | undefined): number {
  const n = Number(mb) || 0;
  return Math.round((n / 1024) * 10) / 10;
}
